import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api.js';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { QRIcon } from '../../components/common/Icons';
import {
  FiEye,
  FiShoppingBag,
  FiMessageSquare,
  FiCheckCircle,
  FiPlus,
  FiEdit,
  FiExternalLink,
  FiCreditCard
} from 'react-icons/fi';

const DashboardOverview = () => {
  const { business } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await api.get('/analytics');
        if (res.success) {
          setStats(res.data);
        }
      } catch (err) {
        console.error('Analytics fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) return <LoadingSpinner text="Loading dashboard metrics..." />;

  const publicPageUrl = business ? `/business/${business.slug}` : '#';

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.85rem', fontWeight: 800 }}>
          Dashboard Overview
        </h1>
        <p style={{ color: '#94A3B8', fontSize: '0.95rem' }}>
          Welcome back! Here is a summary of your digital business performance.
        </p>
      </div>

      {/* Completion Indicator */}
      {stats && (
        <div className="completion-banner">
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '700', fontSize: '1rem' }}>
                Profile Completion: {stats.completionPercentage}%
              </span>
              <span style={{ fontSize: '0.85rem', color: 'var(--secondary-500)', fontWeight: '600' }}>
                {stats.completionPercentage === 100 ? '100% Complete!' : 'Complete your profile for best customer conversion'}
              </span>
            </div>
            <div className="completion-bar-outer">
              <div className="completion-bar-inner" style={{ width: `${stats.completionPercentage}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon-bg" style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#6366F1' }}>
            <FiShoppingBag />
          </div>
          <div>
            <div className="stat-val">{stats?.totalProducts || 0}</div>
            <div className="stat-lbl">Total Products</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-bg" style={{ background: 'rgba(6, 182, 212, 0.15)', color: '#06B6D4' }}>
            <FiMessageSquare />
          </div>
          <div>
            <div className="stat-val">{stats?.totalEnquiries || 0}</div>
            <div className="stat-lbl">Total Enquiries</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-bg" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#F59E0B' }}>
            <FiEye />
          </div>
          <div>
            <div className="stat-val">{stats?.views || 0}</div>
            <div className="stat-lbl">Profile Views</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-bg" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10B981' }}>
            <FiCheckCircle />
          </div>
          <div>
            <div className="stat-val">
              <span className={`badge ${stats?.isPublished ? 'badge-success' : 'badge-warning'}`}>
                {stats?.isPublished ? 'Published' : 'Draft'}
              </span>
            </div>
            <div className="stat-lbl">Page Status</div>
          </div>
        </div>
      </div>

      {/* Analytics Interaction Counters */}
      <div className="glass-card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.2rem', fontWeight: 700, marginBottom: '1.25rem' }}>
          Customer Interaction Performance
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
          <div style={{ padding: '1.25rem', background: 'rgba(0, 0, 0, 0.03)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)' }}>
            <span style={{ fontSize: '0.85rem', color: '#94A3B8' }}>WhatsApp Clicks</span>
            <h4 style={{ fontSize: '1.6rem', fontWeight: '800', color: '#25D366', marginTop: '0.25rem' }}>
              {stats?.whatsappClicks || 0}
            </h4>
          </div>

          <div style={{ padding: '1.25rem', background: 'rgba(0, 0, 0, 0.03)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)' }}>
            <span style={{ fontSize: '0.85rem', color: '#94A3B8' }}>Phone Call Clicks</span>
            <h4 style={{ fontSize: '1.6rem', fontWeight: '800', color: '#3B82F6', marginTop: '0.25rem' }}>
              {stats?.phoneClicks || 0}
            </h4>
          </div>

          <div style={{ padding: '1.25rem', background: 'rgba(0, 0, 0, 0.03)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)' }}>
            <span style={{ fontSize: '0.85rem', color: '#94A3B8' }}>New Unread Enquiries</span>
            <h4 style={{ fontSize: '1.6rem', fontWeight: '800', color: '#EC4899', marginTop: '0.25rem' }}>
              {stats?.newEnquiries || 0}
            </h4>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="glass-card">
        <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.2rem', fontWeight: 700, marginBottom: '1.25rem' }}>
          Quick Actions
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
          <Link to="/dashboard/products" className="btn btn-secondary btnn" style={{ justifyContent: 'flex-start' }}>
            <FiPlus /> Add Product
          </Link>
          <Link to="/dashboard/business" className="btn btn-secondary btnn" style={{ justifyContent: 'flex-start' }}>
            <FiEdit /> Edit Business
          </Link>
          <a href={publicPageUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btnn" style={{ justifyContent: 'flex-start' }}>
            <FiExternalLink /> View Business Page
          </a>
          <Link to="/dashboard/qr-code" className="btn btn-secondary btnn" style={{ justifyContent: 'flex-start' }}>
            <QRIcon /> Generate QR Code
          </Link>
          <Link to="/dashboard/digital-card" className="btn btn-secondary btnn" style={{ justifyContent: 'flex-start' }}>
            <FiCreditCard /> Download Digital Card
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
